import React, { useRef, useState } from 'react';
import { QuickCreateDialog } from '@/components/QuickCreateDialog.jsx';
import { PipelineQuickCreateDialog } from '@/components/PipelineQuickCreateDialog.jsx';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { permissions } from '@vanilla/services/PermissionService.js';
import { QC_EDIT_ICON, QC_PLUS_ICON, fetchEntityForEdit } from '@/lib/quickCreateHelpers.js';
import { ToastManager } from '@/lib/toast.js';
import { UserService } from '@vanilla/services/UserService.js';
import { PipelineStore } from '@vanilla/constants/Pipelines.js';
import { PipelinesApiService } from '@vanilla/services/crm/PipelinesApiService.js';

/**
 * True when a Quick Create / pipeline popup (or its backdrop) is the interaction target,
 * or any such popup is currently open — used to keep parent Radix Dialogs from dismissing.
 */
export function isQuickCreateInteraction(target) {
    if (document.body.classList.contains('qc-popup-open')) return true;
    if (document.querySelector('.quick-create-popup.active')) return true;
    if (!(target instanceof Element)) return false;
    return Boolean(
        target.closest(
            '.quick-create-popup, .quick-create-popup-backdrop, .quick-create-btn, .quick-create-btn-group',
        ),
    );
}

const EMPTY_SELECT_VALUE = '__empty__';

function optionItemValue(opt) {
    return opt.value === '' || opt.value == null ? EMPTY_SELECT_VALUE : String(opt.value);
}

/**
 * Radix Select styled for Deal Form density (replaces NativeSelect).
 * Empty options (value '') are mapped to a sentinel so they can appear as list items.
 */
export function DealFormSelect({
    id,
    value,
    onValueChange,
    placeholder = 'Select…',
    disabled = false,
    options = [],
    allowEmpty: _allowEmpty = true,
    className,
}) {
    const hasEmptyOption = options.some((opt) => opt.value === '' || opt.value == null);
    const resolved = value
        ? String(value)
        : (hasEmptyOption ? EMPTY_SELECT_VALUE : undefined);
    return (
        <Select
            value={resolved}
            disabled={disabled}
            onValueChange={(v) => onValueChange(v === EMPTY_SELECT_VALUE ? '' : v)}
        >
            <SelectTrigger
                id={id}
                className={cn(
                    'form-input h-[var(--control-height,32px)] min-h-[var(--control-height,32px)] w-full',
                    className,
                )}
            >
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent className="z-[1100]">
                {options.map((opt) => {
                    const itemValue = optionItemValue(opt);
                    return (
                        <SelectItem key={itemValue} value={itemValue} disabled={!!opt.disabled}>
                            {opt.label}
                        </SelectItem>
                    );
                })}
            </SelectContent>
        </Select>
    );
}

function moduleForEntity(entityType) {
    if (entityType === 'user') return 'users';
    if (entityType === 'account') return 'accounts';
    if (entityType === 'contact') return 'contacts';
    if (entityType === 'pipeline') return 'pipeline';
    return null;
}

/**
 * Permission-gated + / edit icons beside a Radix Select.
 * Uses React QuickCreateDialog (Radix) — not the vanilla QuickCreatePopup class.
 */
export function DealQuickCreateField({ entityType, value, onCreated, children }) {
    const addRef = useRef(null);
    const editRef = useRef(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogEditEntity, setDialogEditEntity] = useState(null);
    const moduleKey = moduleForEntity(entityType);
    const canCreate = !moduleKey || permissions.canCreate(moduleKey);
    const canEdit = !moduleKey || permissions.canEdit(moduleKey);

    if (!canCreate && !canEdit) {
        return children;
    }

    const onAdd = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (moduleKey && !permissions.canCreate(moduleKey)) {
            ToastManager.error('Permission denied', `You don't have permission to create ${entityType}s.`);
            return;
        }
        setDialogEditEntity(null);
        setDialogOpen(true);
    };

    const onEdit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!value) return;
        if (moduleKey && !permissions.canEdit(moduleKey)) {
            ToastManager.error('Permission denied', `You don't have permission to edit ${entityType}s.`);
            return;
        }
        let editEntity = await fetchEntityForEdit(entityType, value);
        if (entityType === 'user' && value) {
            try {
                const fresh = await UserService.getById(value);
                if (fresh) editEntity = { ...(editEntity || {}), ...fresh };
            } catch {
                /* keep fetched */
            }
        }
        setDialogEditEntity(editEntity);
        setDialogOpen(true);
    };

    const stopNestedDismiss = (e) => {
        e.stopPropagation();
    };

    return (
        <>
            <div className="select-with-icon quick-create-wrapper">
                <div className="deal-form-select-grow" style={{ flex: 1, minWidth: 0 }}>
                    {children}
                </div>
                <div className="quick-create-btn-group">
                    {canCreate && (
                        <button
                            ref={addRef}
                            type="button"
                            className="quick-create-btn quick-create-add-btn"
                            data-entity-type={entityType}
                            aria-label={`Create new ${entityType}`}
                            title={`Create new ${entityType}`}
                            dangerouslySetInnerHTML={{ __html: QC_PLUS_ICON }} // eslint-disable-line react/no-danger -- QC_PLUS_ICON static SVG (no user input)
                            onPointerDown={stopNestedDismiss}
                            onClick={onAdd}
                        />
                    )}
                    {canEdit && (
                        <button
                            ref={editRef}
                            type="button"
                            className="quick-create-btn quick-create-edit-btn"
                            data-entity-type={entityType}
                            aria-label={`Edit ${entityType}`}
                            title={`Edit ${entityType}`}
                            disabled={!value}
                            dangerouslySetInnerHTML={{ __html: QC_EDIT_ICON }} // eslint-disable-line react/no-danger -- QC_EDIT_ICON static SVG
                            onPointerDown={stopNestedDismiss}
                            onClick={onEdit}
                        />
                    )}
                </div>
            </div>
            <QuickCreateDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                entityType={entityType}
                editEntity={dialogEditEntity}
                onSave={async (entity) => { if (onCreated) await onCreated(entity); }}
            />
        </>
    );
}

/** Pipeline + / edit — now uses React PipelineQuickCreateDialog (Radix) instead of vanilla DOM popup. */
export function DealPipelineQuickCreateField({ value, onSaved, children }) {
    const canCreate = permissions.canCreate('pipeline');
    const canEdit = permissions.canEdit('pipeline');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogEditEntity, setDialogEditEntity] = useState(null);

    if (!canCreate && !canEdit) {
        return children;
    }

    const onAdd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDialogEditEntity(null);
        setDialogOpen(true);
    };

    const onEdit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!value) {
            ToastManager.info('Select a pipeline', 'Choose a pipeline first, then click edit.');
            return;
        }
        let pipeline = PipelineStore.get(value);
        if (!pipeline) {
            try {
                const response = await PipelinesApiService.getById(value);
                pipeline = response?.data || response;
                if (pipeline?.id) PipelineStore.add(pipeline);
            } catch (err) {
                console.warn('Failed to fetch pipeline for edit:', err);
            }
        }
        if (!pipeline) {
            ToastManager.error('Pipeline not found', 'Could not load the selected pipeline for editing.');
            return;
        }
        setDialogEditEntity(pipeline);
        setDialogOpen(true);
    };

    const stopNestedDismiss = (e) => {
        e.stopPropagation();
    };

    return (
        <>
            <div className="select-with-icon quick-create-wrapper">
                <div className="deal-form-select-grow" style={{ flex: 1, minWidth: 0 }}>
                    {children}
                </div>
                <div className="quick-create-btn-group">
                    {canCreate && (
                        <button
                            type="button"
                            className="quick-create-btn quick-create-add-btn"
                            data-entity-type="pipeline"
                            aria-label="Create new pipeline"
                            title="Create new pipeline"
                            dangerouslySetInnerHTML={{ __html: QC_PLUS_ICON }} // eslint-disable-line react/no-danger -- QC_PLUS_ICON static SVG
                            onPointerDown={stopNestedDismiss}
                            onClick={onAdd}
                        />
                    )}
                    {canEdit && (
                        <button
                            type="button"
                            className="quick-create-btn quick-create-edit-btn"
                            data-entity-type="pipeline"
                            aria-label="Edit pipeline"
                            title="Edit pipeline"
                            disabled={!value}
                            dangerouslySetInnerHTML={{ __html: QC_EDIT_ICON }} // eslint-disable-line react/no-danger -- QC_EDIT_ICON static SVG
                            onPointerDown={stopNestedDismiss}
                            onClick={onEdit}
                        />
                    )}
                </div>
            </div>
            <PipelineQuickCreateDialog open={dialogOpen} onOpenChange={setDialogOpen} editEntity={dialogEditEntity} onSaved={onSaved} />
        </>
    );
}
